// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { IState } from "states/states";
import { ActionType, IAction, IActionChangeInput, raiseAction } from "actions";
import { Bus, IInput, IModule, InputType, InputUnit } from "parameter";
import { makeCommandSeqNr, tabClickHandler } from "guiutils";

import InputModel, { IOState } from "model/input";

import { ReactComponent as IconOff } from "static/iconOff.svg";
import { ReactComponent as IconOn } from "static/iconOn.svg";
import { ReactComponent as IconClock } from "static/iconClock.svg";
import { ReactComponent as IconUnknown } from "static/iconUnknown.svg";
import { ReactComponent as IconEdit } from "static/iconEdit.svg";
import { Link } from "react-router-dom";
import { MainPanel } from "types";

interface IInputExtProps {
  index: number;
  isEditable: boolean;
  showModule: boolean;
}

interface IInputProps {
  parameter: IInput;
  rawValue: number | undefined;
  timestamp: number;
  modules: IModule[] | undefined;
}

const mapStateToProps = (state: IState, ownProps: IInputExtProps) => {
  const props: IInputProps = {
    parameter: state.parameter!.input[ownProps.index],
    rawValue: state.data.input[ownProps.index],
    timestamp: state.data.timestamp,
    modules: state.parameter?.module,
  };
  return props;
};

interface IInputDispatch {
  onEdit: () => void;
  onChange: (value: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void, ownProps: IInputExtProps) => {
  const dispatchProps: IInputDispatch = {
    onEdit: () => {
      dispatch(raiseAction(ActionType.ACT_EDIT_INPUT, ownProps.index));
    },
    onChange: (value: number) => {
      const payload: IActionChangeInput = {
        commandSeqNr: makeCommandSeqNr(),
        index: ownProps.index,
        value,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_INPUT_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msgEdit: {
    id: "change value",
  },
});

class InputComponent extends React.Component<
  IInputProps & IInputExtProps & IInputDispatch & WrappedComponentProps
> {
  public render() {
    const input = new InputModel(this.props.parameter, this.props.rawValue, this.props.timestamp);
    const hasValue = input.parameter.type === InputType.ANALOG;
    const isEditable =
      this.props.isEditable && input.parameter.webCtrl && this.isChangeable() && !input.isOffline();
    const module =
      this.props.parameter.bus === Bus.MODULE
        ? this.props.modules?.find((curItem) => curItem.id === this.props.parameter.module)
        : undefined;

    return (
      <tr className="inputitem">
        <td>{this.props.parameter.name}</td>
        {this.props.showModule && (
          <td className="optional">
            <Link to={`../${MainPanel.MODULES}`} style={{ textDecoration: "none" }}>
              {module?.name}
            </Link>
          </td>
        )}
        <td
          className={`icon-col ${hasValue ? "" : "right"} ${isEditable ? "img-link" : ""}`}
          colSpan={hasValue ? 1 : 2}
          title={input.getText()}
          onClick={
            isEditable
              ? input.parameter.type === InputType.ANALOG
                ? this.props.onEdit
                : () => {
                    this.props.onChange(input.state === IOState.OFF ? 1 : 0);
                  }
              : undefined
          }
        >
          {this.getIcon(input)}
        </td>
        {hasValue && (
          <td className="value-col">
            {input.parameter.type === InputType.ANALOG && input.getText()}
          </td>
        )}
        {isEditable ? (
          <td
            tabIndex={0}
            onKeyDown={tabClickHandler}
            onClick={this.props.onEdit}
            className="edit-col img-link"
            title={this.props.intl.formatMessage(messages.msgEdit)}
          >
            <IconEdit />
          </td>
        ) : (
          <td className="edit-col"></td>
        )}
      </tr>
    );
  }

  private getIcon(input: InputModel) {
    if (input.parameter.type === InputType.ANALOG) {
      return input.parameter.unit &&
        ["date", "time", "timestamp"].includes(input.parameter.unit) ? (
        <IconClock />
      ) : (
        <></>
      );
    }
    if (input.state === IOState.OFF) return <IconOff />;
    if (input.state === IOState.ON) return <IconOn />;
    return <IconUnknown />;
  }

  private isChangeable() {
    return this.props.parameter.bus === Bus.VIRTUAL;
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(InputComponent));
